'use client';

import dynamic from 'next/dynamic';
import type React from 'react';
import { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import Footer from 'src/components/Footer';
import Header from 'src/components/Header';
import PWAPrompt from 'src/components/PWAPrompt';
import PushNotificationPrompt from 'src/components/PushNotificationPrompt';
import { WalletAuthHandler } from 'src/components/WalletAuthHandler';
import WalletConfirmationOverlay from 'src/components/WalletConfirmationOverlay';
import { AdminProvider } from 'src/context/AdminContext';
import { MobileMenuProvider } from 'src/context/MobileMenuContext';
import { ProfileProvider } from 'src/context/ProfileContext';
import { SignatureProvider } from 'src/context/SignatureContext';
import { SocialProvider } from 'src/context/SocialContext';
import { WalletInitializationProvider } from 'src/context/WalletInitializationContext';

const OnchainProviders = dynamic(
  () => import('src/components/OnchainProviders'),
  {
    ssr: false,
  },
);

export default function RootLayoutClient({
  children,
}: {
  children: React.ReactNode;
}) {
  useEffect(() => {
    // Handle fullscreen for PWA
    const handleFullscreen = () => {
      if (
        document.documentElement.requestFullscreen &&
        window.matchMedia('(display-mode: standalone)').matches
      ) {
        document.documentElement.requestFullscreen().catch(() => {
          // Silently fail if fullscreen is not allowed
        });
      }
    };

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', handleFullscreen);
    // Listen for focus changes
    window.addEventListener('focus', handleFullscreen);

    return () => {
      document.removeEventListener('visibilitychange', handleFullscreen);
      window.removeEventListener('focus', handleFullscreen);
    };
  }, []);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
    }
  }, []);

  return (
    <div className='safe-top safe-bottom flex min-h-screen flex-col'>
      <MobileMenuProvider>
        <OnchainProviders>
          <SignatureProvider>
            <WalletInitializationProvider>
              <AdminProvider>
                <ProfileProvider>
                  <SocialProvider>
                    <WalletAuthHandler />
                    <Header title="" />
                    <WalletConfirmationOverlay />
                    <PushNotificationPrompt />
                    <main className="flex-1">{children}</main>
                    <Footer />
                  </SocialProvider>
                </ProfileProvider>
              </AdminProvider>
            </WalletInitializationProvider>
          </SignatureProvider>
        </OnchainProviders>
      </MobileMenuProvider>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={true}
        draggable={true}
        pauseOnHover={true}
        theme="dark"
        toastStyle={{
          backgroundColor: 'rgba(31, 41, 55, 0.95)',
          backdropFilter: 'blur(8px)',
        }}
      />
      <div id="modal-root" />
      <PWAPrompt />
    </div>
  );
}
